import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { catchError, defer, distinctUntilChanged, EMPTY, filter, finalize, forkJoin, from, iif, map, merge, mergeMap, of, shareReplay, switchMap, take, tap, toArray, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { selectedWidget, workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NotiDetails, NotificationsData, NotiSiteResponse } from 'src/core/types/servicehistory.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalService } from 'src/core/services/modal.service';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSystem } from 'src/core/types/selectedSystem.types';
import ExcelJS from 'exceljs';
import { SubscriptionService } from 'src/core/services/subscription.service';
import type { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { shouldMarkPromptAsOptionAnswer } from 'src/core/utils/chat-option-answer.util';

@Component({
  selector: 'app-ms-notifications',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './ms-notifications.component.html',
  styleUrl: './ms-notifications.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MsNotificationsComponent {

  //Table Variables
  @ViewChild('table') table: Table;
  readonly businessPartnerMode = environment.businessPartnerMode;

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private modalService: ModalService,
    private selectedContentService: SelectedContentService,
    private subscriptionService: SubscriptionService,
    private translocoService: TranslocoService
  ) { }

  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  isDeviceUI: boolean;
  notifications;
  expandedRows: any = {};
  searchTerm: string = '';
  selectedRange: number;
  isDownloading: boolean = false;
  downloadError: string | null = null;
  downloadHadErrors: boolean = false;
  get cols() {
    return [
      { field: 'id', header: this.translate('msNotifications.table.notificationId') },
      { field: 'serial', header: this.translate('msNotifications.table.serialNumber') },
      { field: 'issue', header: this.translate('msNotifications.table.issue') },
      { field: 'age', header: this.translate('msNotifications.table.age') },
      { field: 'type', header: this.translate('msNotifications.table.type') },
      { field: 'status', header: this.translate('msNotifications.table.status') },
      { field: 'opened', header: this.translate('msNotifications.table.opened') },
      { field: 'closed', header: this.translate('msNotifications.table.closed') },
      { field: 'visits', header: this.translate('msNotifications.table.activities') },
      { field: 'labor', header: this.translate('msNotifications.table.laborHours') },
      { field: 'travel', header: this.translate('msNotifications.table.travelHours') },
      { field: 'parts', header: this.translate('msNotifications.table.partsCost') },
      { field: 'original notification text', header: this.translate('msNotifications.table.originalNotificationText') },
      { field: 'ai summary', header: this.translate('msNotifications.table.aiSummary') }
    ];
  }
   // const headers = [
  //           'Notification ID',
  //           'Issue',
  //           'Age',
  //           'Type',
  //           'Status',
  //           'Opened',
  //           'Closed',
  //           'Activities',
  //           'Labor (h)',
  //           'Travel (h)',
  //           'Parts (€)',
  //           'Original Notification Text',
  //           'AI Summary'
  //         ]

  private createDownloadId(): string {
    try {
      // modern browsers
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyCrypto: any = (globalThis as any)?.crypto;
      if (anyCrypto?.randomUUID) return anyCrypto.randomUUID();
    } catch { }

    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  private triggerExcelDownload(buffer: ArrayBuffer | Uint8Array, filename: string) {
    const content = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    // Ensure we pass a real ArrayBuffer (not ArrayBufferLike/SharedArrayBuffer) to Blob for TS + browser compatibility
    const arrayBuffer = new ArrayBuffer(content.byteLength);
    new Uint8Array(arrayBuffer).set(content);

    const blob = new Blob([arrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Cleanup (async so the download can start)
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 0);
  }

  private pushDownloadSnack(
    id: string,
    type: 'warning' | 'success' | 'error',
    title: string,
    message: string
  ) {
    const entry = {
      id,
      type,
      title,
      message,
      timestamp: new Date().toISOString(),
      read: false
    };

    const upsertById = (list: any[]) => {
      const existingIndex = (list ?? []).findIndex((n: any) => n?.id === id);
      if (existingIndex >= 0) {
        const next = [...list];
        next[existingIndex] = { ...next[existingIndex], ...entry };
        return next;
      }
      return [...(list ?? []), entry];
    };

    const current = this.subscriptionService.$notifications.getValue();
    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

    this.subscriptionService.$notifications.next(upsertById(current));
    this.subscriptionService.$notificationsCenter.next(upsertById(currentCenter));
  }

  @Input() tabs: any[] = [];
  selectedWidget: string = "None";
  displayedTabs: any[] = [];
  activeTab: string = "Activities";
  workareaSize: workareaSize = "small";
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
  onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }  
  switchTab = this.widgetService.switchTab.bind(this.widgetService);
  changeRange = (range: number, type: 'systemHealth' | 'serviceHistory') => {
    this.daterangeService.changeRange(range, type);
  };
  private destroyRef = inject(DestroyRef);

  private translate(key: string, params?: Record<string, string | number>): string {
    return this.translocoService.translate(key, params);
  }

  private getExportHeaders(includeSerial: boolean): string[] {
    const headers = [
      this.translate('msNotifications.table.notificationId'),
      this.translate('msNotifications.table.issue'),
      this.translate('msNotifications.table.age'),
      this.translate('msNotifications.table.type'),
      this.translate('msNotifications.table.status'),
      this.translate('msNotifications.table.opened'),
      this.translate('msNotifications.table.closed'),
      this.translate('msNotifications.table.activities'),
      this.translate('msNotifications.table.laborHours'),
      this.translate('msNotifications.table.travelHours'),
      this.translate('msNotifications.table.partsCost'),
      this.translate('msNotifications.table.originalNotificationText'),
      this.translate('msNotifications.table.aiSummary')
    ];

    if (!includeSerial) {
      return headers;
    }

    return [headers[0], this.translate('msNotifications.table.serialNumber'), ...headers.slice(1)];
  }


  ngOnInit(): void {
    this.loadingService.$isNotificationsLoading.subscribe((loading) => {
      this.isLoading = loading;
    });
    this.loadingService.$isNotificationsError.subscribe((error) => {
      this.isError = error;
    });

    //Check which tabs to display
    this.widgetService.$selectedWidget.subscribe({
      next: (widget: selectedWidget) => {
        this.selectedWidget = widget;
        this.displayedTabs = this.tabs.filter(tab => tab.selectedWidget === widget);
      }
    });

    //Check which tab is currently active
    this.widgetService.$activeTab.subscribe((tab) => {
      if (tab) {
        this.activeTab = tab;
      }
      else {
        this.activeTab = this.displayedTabs[0]?.name;
      }
    });

    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to changed Daterange
    const range$ = this.daterangeService.$selectedRangeServiceHistory;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshNotifications.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isNotificationsLoading.next(true);
        this.loadingService.$isNotificationsError.next(false);
        this.loadingService.$isNotificationsEmpty.next(false);
        this.notifications = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.NotiPullDate;
        const pulledFor = this.selectedSystemService.notiPulledFor;

        const isDevice = this.selectedContentService.selection.kind == "device";
        this.isDeviceUI = isDevice;
        const serial = this.selectedContentService.currentSystem?.SerialNumber || '';
        const material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        const funcloc = this.selectedContentService.currentSite?.Id;

        const cacheKey = isDevice ? (serial + material) : funcloc;
        const shouldFetch = force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== cacheKey;

        type NotificationsResponse = NotificationsData | NotiSiteResponse;

        const apiCall$: Observable<ApiResponse<NotificationsResponse>> = isDevice
          ? this.dataService.getNotifications(material, serial).pipe(
            map((result): ApiResponse<NotificationsResponse> => result)
          )
          : this.dataService.getNotificationsSite(funcloc).pipe(
            map((result): ApiResponse<NotificationsResponse> => result)
          );

        return iif(
          () => shouldFetch,
          apiCall$.pipe(
            tap(({ response, status }: ApiResponse<NotificationsResponse>) => {
              console.log(force ? 'Forced refresh of notifications data' : 'Fetching new notifications data');
              this.selectedSystemService.NotiPullDate = now;
              this.selectedSystemService.notiPulledFor = cacheKey;
              this.selectedSystemService.notiPulledFor = cacheKey;
              if (status === 200) {
                this.selectedSystemService.$notifications.next(response);
              } else if (status === 204) {
                this.selectedSystemService.$notifications.next(null);
                this.loadingService.$isNotificationsEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$notifications.pipe(
            take(1),
            tap(() => console.log('Using cached notifications data')),
            map((cached): ApiResponse<NotificationsResponse> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response }: ApiResponse<NotificationsResponse>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          if (!response) {
            this.notifications = [];
            this.loadingService.$isNotificationsEmpty.next(true);
            this.loadingService.$isNotificationsLoading.next(false);
            return;
          }

          if (this.selectedContentService.selection.kind == "device") {
            const notifData = response as NotificationsData;
            const mappedNotifications = notifData?.notifications?.map(noti => ({
              ...noti,
              age: this.calculateAge(noti.open_date),
            })) || [];
            this.notifications = mappedNotifications.filter(f => f.time_window.some(tw => tw === (rangeValue === 365 ? 120 : rangeValue)));
          }

          if (this.selectedContentService.selection.kind == "site") {
            const notifData = response as NotiSiteResponse;

            const mappedNotifications = 
              notifData?.notifications.map(noti => ({
                ...noti,
                serial_number: noti.serial_number,
                material_number: noti.material_number,
                age: this.calculateAge(noti.opened_date)
              })) || [];

            this.notifications = mappedNotifications.filter(f => f.time_window.some(tw => tw === (rangeValue === 365 ? 120 : rangeValue)));
          }

          this.loadingService.$isNotificationsLoading.next(false);

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.notifications = [];
          this.loadingService.$isNotificationsError.next(true);
          this.loadingService.$isNotificationsLoading.next(false);
        }
      }
    );

    this.selectedSystemService.$selectedNotification.pipe(
      // ignore falsy & repeated selections
      filter((id): id is string => !!id),
      distinctUntilChanged(),

      // pull in current cache and the selected system (for material/serial)
      withLatestFrom(
        this.selectedSystemService.$notificationDetails,   // NotiDetails[]
        this.selectedSystemService.$notificationSummary   // Summary[] (assumed existing)
      ),

      // log if cached
      tap(([id, details, summary]) => {
        if ((details ?? []).some(d => d.id === id)) {
          console.log('Using cached notification details for', id);
        }
        if ((summary ?? []).some((s: any) => s?.id === id)) {
          console.log('Using cached notification summary for', id);
        }
      }),

      // fetch missing resources (details, summary) in parallel
      switchMap(([id, detailsCache, summaryCache]) => {
        const hasDetails = (detailsCache ?? []).some(d => d.id === id);
        const hasSummary = (summaryCache ?? []).some((s: any) => s?.id === id);

        // Set loading states only for what will be fetched
        if (!hasDetails) {
          this.loadingService.$isNotificationDetailsLoading.next(true);
          this.loadingService.$isNotificationDetailsError.next(false);
        }
        if (!hasSummary) {
          this.loadingService.$isNotificationSummaryLoading.next(true);
          this.loadingService.$isNotificationSummaryError.next(false);
        }

        let serial;
        let material;

        if (this.selectedContentService.selection.kind == "device") {
          serial = this.selectedContentService.currentSystem?.SerialNumber || '';
          material = this.selectedContentService.currentSystem?.MaterialNumber || '';
        } else {
          const rawData = this.selectedSystemService.$notifications.value as NotiSiteResponse;
          const device = rawData?.notifications.find(n => n.id === id);
          serial = device?.serial_number || '';
          material = device?.material_number || '';
        }

        const details$ = iif(
          () => !hasDetails,
          this.dataService.getNotificationDetails(material, serial, id).pipe(
            tap(({ response }: ApiResponse<NotiDetails>) => {
              const current = this.selectedSystemService.$notificationDetails.value ?? [];
              if (!current.some(d => d.id === id)) {
                this.selectedSystemService.$notificationDetails.next([...current, response]);
              }
            }),
            map(() => ({ ok: true })),
            catchError(err => {
              console.error('Failed to fetch notification details', err);
              this.loadingService.$isNotificationDetailsError.next(true);
              // Emit a sentinel so forkJoin waits for both and doesn't cancel the other
              return of({ ok: false, error: err });
            }),
            finalize(() => {
              this.loadingService.$isNotificationDetailsLoading.next(false);
            })
          ),
          // already cached
          defer(() => [{ ok: true }])
        );

        const summary$ = iif(
          () => !hasSummary,
          this.dataService.getNotificationSummary(id).pipe(
            tap(({ response }) => {
              const current = this.selectedSystemService.$notificationSummary.value ?? [];
              if (!current.some((s: any) => s?.id === id)) {
                this.selectedSystemService.$notificationSummary.next([...current, response]);
              }
            }),
            map(() => ({ ok: true })),
            catchError(err => {
              console.error('Failed to fetch notification summary', err);
              this.loadingService.$isNotificationSummaryError.next(true);
              // Emit a sentinel so forkJoin waits for both and doesn't cancel the other
              return of({ ok: false, error: err });
            }),
            finalize(() => {
              this.loadingService.$isNotificationSummaryLoading.next(false);
            })
          ),
          // already cached
          defer(() => [{ ok: true }])
        );

        // Run both in parallel; each may be a no-op if already cached
        return forkJoin([details$, summary$]).pipe(map(() => id));
      }),

      // auto-unsubscribe on destroy
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (id) => console.log('Fetched/cached details+summary for', id)
    });

  }

  calculateAge(date) {
    const inputDate = new Date(date);
    const today = new Date();

    // Clear time parts to avoid partial days affecting the result
    inputDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - inputDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)); // convert ms to days

    return diffDays;
  }

  private computeAgeForExportRow(r: any): number {
    const date = r?.open_date ?? r?.opened_date;
    const computed = date ? this.calculateAge(date) : NaN;
    if (Number.isFinite(computed)) return computed;

    const raw = r?.age;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
  }

  private mapExportRow(r: any, includeSerial: boolean): any[] {
    const base = [
      r?.id ?? '',
      r?.description ?? '',
      typeof r?.age === 'number' && Number.isFinite(r.age) ? r.age : '',
      r?.type ?? '',
      r?.status ?? '',
      r?.open_date ?? r?.opened_date ?? '',
      r?.closed_date ?? '',
      r?.visits ?? '',
      typeof r?.labor === 'number' ? Number(r.labor.toFixed?.(2) ?? r.labor) : (r?.labor ?? ''),
      typeof r?.travel === 'number' ? Number(r.travel.toFixed?.(2) ?? r.travel) : (r?.travel ?? ''),
      typeof r?.parts_cost === 'number' ? Number(r.parts_cost.toFixed?.(2) ?? r.parts_cost) : (r?.parts_cost ?? ''),
      r?.notificationDetailsSummary ?? '',
      r?.notificationSummarySummary ?? ''
    ];

    if (!includeSerial) {
      return base;
    }

    return [base[0], r?.serial_number ?? '', ...base.slice(1)];
  }

  private async generateAndDownloadWorkbook(headers: string[], rowsAoA: any[][], filename: string): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Notifications');

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };

    for (const row of rowsAoA) {
      worksheet.addRow(row);
    }

    worksheet.columns = (worksheet.columns ?? []).map((col) => {
      let max = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell?.value;
        const len = v == null ? 0 : String(v).length;
        if (len > max) max = len;
      });
      col.width = Math.min(Math.max(max + 2, 10), 60);
      return col;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    this.triggerExcelDownload(buffer, filename);
  }

  private reopenNotificationsCenter() {
    this.subscriptionService.$closeNotifications.next();
    setTimeout(() => {
      this.subscriptionService.$openNotifications.next();
    }, 600);
  }

  private finalizeDownload(snackId: string, filePrepared: boolean, hadRowError: boolean) {
    if (!filePrepared) {
      this.pushDownloadSnack(
        snackId,
        'error',
        this.translate('msNotifications.download.errorTitle'),
        this.translate('msNotifications.download.tryAgainLater')
      );
    } else {
      this.pushDownloadSnack(
        snackId,
        'success',
        this.translate('msNotifications.download.successTitle'),
        hadRowError
          ? this.translate('msNotifications.download.successMessagePartial')
          : this.translate('msNotifications.download.successMessageComplete')
      );
    }

    this.reopenNotificationsCenter();
  }

  private handleEnrichedRowsExport(
    enrichedRows$: Observable<any[]>,
    snackId: string,
    headers: string[],
    includeSerial: boolean,
    filename: string,
    getHadRowError: () => boolean
  ) {
    enrichedRows$.pipe(take(1)).subscribe({
      next: async (enriched) => {
        let filePrepared = false;

        try {
          const rowsAoA = enriched.map((r: any) => this.mapExportRow(r, includeSerial));
          await this.generateAndDownloadWorkbook(headers, rowsAoA, filename);
          this.downloadHadErrors = getHadRowError();
          filePrepared = true;
        } catch (e: any) {
          this.downloadError = e?.message ?? 'Failed to generate Excel file.';
        } finally {
          this.isDownloading = false;
          this.finalizeDownload(snackId, filePrepared, getHadRowError());
        }
      },
      error: (err) => {
        this.downloadError = err?.message ?? 'Download failed.';
        this.pushDownloadSnack(
          snackId,
          'error',
          this.translate('msNotifications.download.errorTitle'),
          this.translate('msNotifications.download.tryAgainLater')
        );
        this.isDownloading = false;
        this.reopenNotificationsCenter();
      }
    });
  }

  private failDownloadPreparation(snackId: string, message: string): Observable<any[]> {
    this.isDownloading = false;
    this.downloadError = message;
    this.pushDownloadSnack(
      snackId,
      'error',
      this.translate('msNotifications.download.errorTitle'),
      this.translate('msNotifications.download.tryAgainLater')
    );
    return of([]);
  }

  askChat(id: string) {

    let prompt = `What can you tell me about the Service Ticket with ID ${id}?`;

    if (shouldMarkPromptAsOptionAnswer(this.chatService.messages?.value)) {
      this.chatService.addMessage(prompt, true, undefined, true);
      return;
    }

    this.chatService.addMessage(prompt, true);

  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  refresh() {
    this.selectedSystemService.$refreshNotifications.next();
  }

  focusNotification(noti_id) {
    this.selectedSystemService.$selectedNotification.next(noti_id);
    this.modalService.$modalChanged.next({ modal: 'notification-details', open: true });
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {
        case 'opened': // assuming this is a date field
          val1 = new Date(a.opened_date);
          val2 = new Date(b.opened_date);
          break;

        case 'serial': // string field
          val1 = a.serial_number?.toLowerCase() ?? '';  
          val2 = b.serial_number?.toLowerCase() ?? '';
          break;

        case 'closed': // assuming this is a date field
          val1 = new Date(a.closed_date);
          val2 = new Date(b.closed_date);
          break;

        case 'id': // string field
          val1 = a.id?.toLowerCase() ?? '';
          val2 = b.id?.toLowerCase() ?? '';
          break;
        case 'issue': // string field
          val1 = a.description?.toLowerCase() ?? '';
          val2 = b.description?.toLowerCase() ?? '';
          break;
        case 'type': // string field
          val1 = a.type?.toLowerCase() ?? '';
          val2 = b.type?.toLowerCase() ?? '';
          break;
        case 'status': // string field
          val1 = a.status?.toLowerCase() ?? '';
          val2 = b.status?.toLowerCase() ?? '';
          break;

        case 'age':
          val1 = this.calculateAge(a.opened_date) || 0;
          val2 = this.calculateAge(b.opened_date) || 0;
          break;
        case 'visits':
          val1 = a.visits || 0;
          val2 = b.visits || 0;
          break;
        case 'labor':
          val1 = a.labor || 0;
          val2 = b.labor || 0;
          break;
        case 'travel':
          val1 = a.travel || 0;
          val2 = b.travel || 0;
          break;
        case 'cost':
          val1 = a.cost || 0;
          val2 = b.cost || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  download() {
    // UI state
    this.isDownloading = true;
    this.downloadError = null;
    this.downloadHadErrors = false;

    const downloadId = this.createDownloadId();
    const snackId = `download${downloadId}`;
 

    //Get current view of the table
    const current = this.table?.filteredValue ?? this.table?.value;
    const rows: any[] = Array.isArray(current) ? current : [];

    const isDevice = this.isDeviceUI;
    const system = this.selectedContentService.currentSystem;
    const site = this.selectedContentService.currentSite;

    const warningMessage = isDevice
      ? this.translate('msNotifications.download.preparingMessageDevice')
      : this.translate('msNotifications.download.preparingMessageSite');

    this.pushDownloadSnack(
      snackId,
      'warning',
      this.translate('msNotifications.download.preparingTitle'),
      warningMessage
    );

    if (rows.length === 0) {
      return this.failDownloadPreparation(snackId, 'No rows to export.');
    }

    const material = system?.MaterialNumber;
    const serial = system?.SerialNumber;
    const funclocID = site?.Id;

    if (isDevice && (!material || !serial)) {
      return this.failDownloadPreparation(snackId, 'Missing system identifiers (material/serial).');
    }

    if (!isDevice && !funclocID) {
      return this.failDownloadPreparation(snackId, 'Missing site identifier (funcloc).');
    }

    let hadRowError = false;
    const markRowError = () => {
      hadRowError = true;
      return of(null);
    };

    const getDetailsSummary$ = (rowData: any, id: string) => {
      const detailsMaterial = isDevice ? material : (rowData?.material_number ?? '');
      const detailsSerial = isDevice ? serial : (rowData?.serial_number ?? '');

      if (!detailsMaterial || !detailsSerial) {
        hadRowError = true;
        return of(null);
      }

      return this.dataService.getNotificationDetails(detailsMaterial, detailsSerial, id).pipe(
        map(({ response }: ApiResponse<NotiDetails>) => response?.details?.summary ?? null),
        catchError(markRowError)
      );
    };

    const enrichedRows$ = from(rows).pipe(
      mergeMap((row) => {
        const { time_window, ...baseRow } = row ?? {};
        const id = baseRow?.id;

        if (!id) {
          return of({
            ...baseRow,
            age: this.computeAgeForExportRow(baseRow),
            notificationDetailsSummary: null,
            notificationSummarySummary: null
          });
        }

        return forkJoin({
          notificationDetailsSummary: getDetailsSummary$(baseRow, id),
          notificationSummarySummary: this.dataService.getNotificationSummary(id).pipe(
            map(({ response }) => response?.summary ?? null),
            catchError(markRowError)
          )
        }).pipe(
          map(({ notificationDetailsSummary, notificationSummarySummary }) => ({
            ...baseRow,
            age: this.computeAgeForExportRow(baseRow),
            notificationDetailsSummary,
            notificationSummarySummary
          }))
        );
      }, 10),
      toArray(),
      shareReplay(1)
    );

    const dateStamp = new Date().toISOString().slice(0, 10);
    const filename = isDevice
      ? `notifications_${String(material ?? 'material').replace(/[^a-z0-9_-]+/gi, '_')}_${String(serial ?? 'serial').replace(/[^a-z0-9_-]+/gi, '_')}_${dateStamp}.xlsx`
      : `notifications_${String(site?.Name ?? 'site').replace(/[^a-z0-9_-]+/gi, '_')}_${dateStamp}.xlsx`;

    const headers = this.getExportHeaders(!isDevice);

    this.handleEnrichedRowsExport(enrichedRows$, snackId, headers, !isDevice, filename, () => hadRowError);
    return enrichedRows$;

  }

}
